import React, { useState, useEffect } from 'react';
import { db, auth } from '../../firebase';
import { ref, onValue, update } from 'firebase/database';
import { useNavigate } from 'react-router-dom';
import EngineerLayout from './EngineerLayout';
import { FaSearch, FaFilter, FaFileDownload, FaEye, FaCopy, FaFileExcel, FaPrint } from 'react-icons/fa';
import { toast } from 'react-toastify';
import * as XLSX from 'xlsx';

const EngineerSocietyList = () => {
    const [registrations, setRegistrations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedTaluka, setSelectedTaluka] = useState('');
    const navigate = useNavigate();

    useEffect(() => {
        const unsubscribe = auth.onAuthStateChanged((user) => {
            if (!user) navigate('/login');
        });

        const registrationsRef = ref(db, 'ekamwatapzp/SocietyRegistrations');
        const unsubData = onValue(registrationsRef, (snapshot) => {
            if (snapshot.exists()) {
                const data = snapshot.val();
                const list = Object.keys(data).map(key => ({
                    id: key,
                    ...data[key]
                }));
                setRegistrations(list.reverse());
            } else {
                setRegistrations([]);
            }
            setLoading(false);
        });

        return () => {
            unsubscribe();
            unsubData();
        };
    }, [navigate]);

    const handleStatusUpdate = async (id, updateData) => {
        const itemRef = ref(db, `ekamwatapzp/SocietyRegistrations/${id}`);
        await update(itemRef, updateData);
    };

    const filteredData = registrations.filter(item => {
        const searchSource = `${item.societyName} ${item.chairmanName}`.toLowerCase();
        const matchesSearch = searchSource.includes(searchTerm.toLowerCase());
        const matchesTaluka = selectedTaluka === '' || item.taluka === selectedTaluka;
        return matchesSearch && matchesTaluka;
    });

    const downloadExcel = () => {
        const exportData = filteredData.map((item, index) => ({
            'अ.क्र.': index + 1,
            'संस्थेचे नाव': item.societyName || '',
            'संस्थेचा पत्ता': item.societyAddress || '',
            'चेअरमेनचे नाव': item.chairmanName || '',
            'चेअरमेन व्हॉट्सॲप': item.chairmanWhatsapp || '',
            'तालुका': item.taluka || '',
            'नोंदणी क्रमांक': item.registrationNo || '',
            'नोंदणी जिल्हा': item.registrationDistrict || '',
            'निबंधक कार्यालय': item.registrarOffice || '',
            'नोंदणी तारीख': item.registrationDate || '',
            'आर्थिक स्थैर्य': item.economicStability || '',
            'स्वतःचे भांडवल': item.ownCapital || '',
            'शासकीय भाग भांडवल': item.govtShare || '',
            'नोंदणी वर्ग': item.registrationClass || '',
            'तपासणी वर्ग': item.inspectionClass || '',
            'ई-मेल': item.email || '',
            'अर्ज सादर तारीख': item.submittedAt ? new Date(item.submittedAt).toLocaleDateString() : '',
            'स्थिती': item.status === 'accepted' ? 'स्वीकारले' : item.status === 'rejected' ? 'नाकारले' : 'प्रलंबित'
        }));

        const ws = XLSX.utils.json_to_sheet(exportData);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "सोसायटी यादी");
        XLSX.writeFile(wb, `सोसायटी_नोंदणी_यादी_${new Date().toLocaleDateString()}.xlsx`);
    };

    const copyToClipboard = () => {
        const headers = ["अ.क्र.", "संस्थेचे नाव", "चेअरमेन", "व्हॉट्सॲप", "तालुका", "नोंदणी क्र."];
        const rows = filteredData.map((item, index) => [
            index + 1,
            item.societyName || '',
            item.chairmanName || '',
            item.chairmanWhatsapp || '',
            item.taluka || '',
            item.registrationNo || ''
        ]);

        const text = [headers, ...rows].map(row => row.join('\t')).join('\n');
        navigator.clipboard.writeText(text).then(() => {
            toast.success("सोसायटी माहिती कॉपी झाली!");
        });
    };

    const handlePrint = () => {
        const printContent = `
            <html>
                <head>
                    <title>सोसायटी नोंदणी यादी - सांगली जिल्हा परिषद</title>
                    <style>
                        @import url('https://fonts.googleapis.com/css2?family=Noto+Sans+Marathi:wght@400;700&display=swap');
                        body { font-family: 'Noto Sans Marathi', sans-serif; padding: 40px; }
                        table { width: 100%; border-collapse: collapse; margin-top: 30px; }
                        th, td { border: 1px solid #e2e8f0; padding: 12px 15px; text-align: left; font-size: 13px; }
                        th { background-color: #f8fafc; color: #475569; font-weight: 700; text-transform: uppercase; }
                        .header { border-bottom: 2px solid #085e53; padding-bottom: 20px; margin-bottom: 30px; }
                        h2 { text-align: center; margin-bottom: 5px; }
                        p { text-align: center; color: #64748b; margin-top: 0; }
                    </style>
                </head>
                <body>
                    <div class="header">
                        <h2>सोसायटी नोंदणी यादी</h2>
                        <p>सांगली जिल्हा परिषद (Zilla Parishad Sangli)</p>
                    </div>
                    <table>
                        <thead>
                            <tr>
                                <th style="text-align: center;">अ.क्र.</th>
                                <th>संस्थेचे नाव</th>
                                <th>चेअरमेन</th>
                                <th>व्हॉट्सॲप</th>
                                <th>तालुका</th>
                                <th>रेजी क्र.</th>
                            </tr>
                        </thead>
                        <tbody>
                            ${filteredData.map((item, index) => `
                                <tr>
                                    <td style="text-align: center;">${index + 1}</td>
                                    <td style="font-weight: 700;">${item.societyName}</td>
                                    <td>${item.chairmanName}</td>
                                    <td>${item.chairmanWhatsapp}</td>
                                    <td>${item.taluka || '-'}</td>
                                    <td>${item.registrationNo || '-'}</td>
                                </tr>
                            `).join('')}
                        </tbody>
                    </table>
                </body>
            </html>
        `;
        const printWindow = window.open('', '_blank');
        printWindow.document.write(printContent);
        printWindow.document.close();
        printWindow.onload = () => { printWindow.print(); printWindow.close(); };
    };

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-600"></div>
            </div>
        );
    }

    return (
        <EngineerLayout>
            <div className="p-6 md:p-8 bg-gray-50/30 min-h-screen">
                {/* Dark Header Card */}
                <div className="bg-[#1a1c23] rounded-3xl p-10 mb-8 shadow-2xl relative overflow-hidden group">
                    <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full -mr-32 -mt-32 blur-3xl transition-transform duration-1000 group-hover:scale-110"></div>
                    <div className="relative flex flex-col md:flex-row justify-between items-center gap-6">
                        <div>
                            <h1 className="text-4xl font-bold text-white tracking-tight mb-3">सोसायटी नोंदणी यादी</h1>
                            <p className="text-gray-400 font-bold text-sm tracking-widest uppercase">
                                सर्व सोसायटी नोंदणी अर्जांची यादी
                            </p>
                        </div>
                        <button 
                            onClick={() => navigate('/engineer/all-users')}
                            className="bg-[#2d2f39] hover:bg-[#383a45] text-white px-8 py-3 rounded-xl text-sm font-bold tracking-widest transition-all hover:scale-105 active:scale-95 shadow-lg border border-white/5"
                        >
                            मागे जा
                        </button>
                    </div>
                </div>

                <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
                    {/* Toolbar */}
                    <div className="p-6 border-b border-gray-50 flex flex-col lg:flex-row justify-between items-center gap-6 bg-white">
                        <div className="flex items-center gap-3">
                            <button onClick={copyToClipboard} className="flex items-center gap-2 px-6 py-2.5 bg-gray-800 text-white rounded-xl text-xs font-bold uppercase tracking-widest hover:bg-black transition-all shadow-md active:scale-95">
                                <FaCopy /> Copy
                            </button>
                            <button onClick={downloadExcel} className="flex items-center gap-2 px-6 py-2.5 bg-emerald-600 text-white rounded-xl text-xs font-bold uppercase tracking-widest hover:bg-emerald-700 transition-all shadow-md active:scale-95">
                                <FaFileExcel /> Excel
                            </button>
                            <button onClick={handlePrint} className="flex items-center gap-2 px-6 py-2.5 bg-blue-600 text-white rounded-xl text-xs font-bold uppercase tracking-widest hover:bg-blue-700 transition-all shadow-md active:scale-95">
                                <FaPrint /> Print
                            </button>
                        </div>

                        <div className="flex items-center gap-4 w-full lg:w-auto">
                            <div className="relative flex-grow">
                                <input 
                                    type="text" 
                                    placeholder="संस्था किंवा चेअरमेननुसार शोधा..." 
                                    className="w-full lg:w-80 pl-12 pr-4 py-3 bg-gray-50 border border-gray-100 rounded-xl text-sm font-bold focus:bg-white focus:border-blue-500 transition-all outline-none text-gray-700"
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                />
                                <FaSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                            </div>
                        </div>
                    </div>

                    {/* Table */}
                    <div className="overflow-x-auto">
                        <table className="w-full text-left border-collapse">
                            <thead>
                                <tr className="bg-gray-50/50 border-b border-gray-100 text-[10px] font-bold text-gray-400 uppercase tracking-[0.2em]">
                                    <th className="px-6 py-5 text-center w-16">#</th>
                                    <th className="px-6 py-5">संस्थेचे नाव</th>
                                    <th className="px-6 py-5 text-center">रेजी क्र.</th>
                                    <th className="px-6 py-5 text-center">प्रशासन</th>
                                    <th className="px-6 py-5 text-center">अभियंता</th>
                                    <th className="px-6 py-5 text-center">लेखापाल</th>
                                    <th className="px-6 py-5 text-center">प्रभारी</th>
                                    <th className="px-6 py-5 text-center">कृती</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-50">
                                {loading ? (
                                    <tr>
                                        <td colSpan="8" className="px-6 py-10 text-center">
                                            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-600 mx-auto"></div>
                                        </td>
                                    </tr>
                                ) : filteredData.map((item, index) => (
                                    <tr key={item.id} className="group hover:bg-blue-50/30 transition-colors">
                                        <td className="px-6 py-6 text-center text-xs font-bold text-gray-400 italic">
                                            {String(index + 1).padStart(2, '0')}
                                        </td>
                                        <td className="px-6 py-6 border-r border-transparent group-hover:border-gray-100 transition-all font-bold text-gray-800 text-sm group-hover:text-blue-700">
                                            {item.societyName}
                                        </td>
                                        <td className="px-6 py-6 text-center">
                                            <span className="text-xs font-bold text-gray-600 tracking-wider font-mono bg-gray-100 px-3 py-1 rounded-full uppercase">
                                                {item.registrationNo}
                                            </span>
                                        </td>
                                        <td className="px-6 py-6 text-center text-xs font-bold">
                                            <span className={item.admin_status === 'Approved' ? 'text-emerald-600' : item.admin_status === 'Rejected' ? 'text-rose-600' : 'text-amber-600'}>
                                                {item.admin_status === 'Approved' ? 'मंजूर' : item.admin_status === 'Rejected' ? 'नाकारले' : 'प्रलंबित'}
                                            </span>
                                        </td>
                                        <td className="px-6 py-6 text-center text-xs font-bold">
                                            <span className={item.engineer_status === 'Approved' ? 'text-emerald-600' : item.engineer_status === 'Rejected' ? 'text-rose-600' : 'text-amber-600'}>
                                                {item.engineer_status === 'Approved' ? 'मंजूर' : item.engineer_status === 'Rejected' ? 'नाकारले' : 'प्रलंबित'}
                                            </span>
                                        </td>
                                        <td className="px-6 py-6 text-center text-xs font-bold">
                                            <span className={item.accountant_status === 'Approved' ? 'text-emerald-600' : item.accountant_status === 'Rejected' ? 'text-rose-600' : 'text-amber-600'}>
                                                {item.accountant_status === 'Approved' ? 'मंजूर' : item.accountant_status === 'Rejected' ? 'नाकारले' : 'प्रलंबित'}
                                            </span>
                                        </td>
                                        <td className="px-6 py-6 text-center text-xs font-bold">
                                            <span className={item.incharge_status === 'Approved' ? 'text-emerald-600' : item.incharge_status === 'Rejected' ? 'text-rose-600' : 'text-amber-600'}>
                                                {item.incharge_status === 'Approved' ? 'मंजूर' : item.incharge_status === 'Rejected' ? 'नाकारले' : 'प्रलंबित'}
                                            </span>
                                        </td>
                                        <td className="px-6 py-6 text-center">
                                            <button 
                                                onClick={() => navigate(`/engineer/societies/details/${item.id}`)}
                                                className="p-2.5 bg-blue-50 text-blue-600 rounded-xl hover:bg-blue-600 hover:text-white transition-all shadow-sm active:scale-95"
                                            >
                                                <FaEye size={14} />
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        {filteredData.length === 0 && !loading && (
                            <div className="py-24 text-center">
                                <h3 className="text-xl font-bold text-gray-300 uppercase tracking-[0.2em]">कोणतीही नोंदणी आढळली नाही.</h3>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </EngineerLayout>
    );
};

export default EngineerSocietyList;
